import * as React from "react";
import {
    createContext,
    Dispatch,
    FC,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from "react";
import { Feature } from "ol";
import { IEsriFieldInfo } from "../Map/rest";
import { Basemaps, DrawType } from "../Map/enums";
import { generateSnapshots } from "../Map/snapshot";
import { IInfo } from "../Popups/Info/Info";
import { IStoredSettings } from "../interfaces";
import { config } from "../Map/config";
import { Geometry } from "ol/geom";

export interface IAppContext {
    hasMap: boolean;
    setHasMap: Dispatch<SetStateAction<boolean>>;
    basemap: Basemaps;
    setBasemap: Dispatch<SetStateAction<Basemaps>>;
    activeTab: number;
    setActiveTab: Dispatch<SetStateAction<number>>;
    fieldInfos: IEsriFieldInfo[];
    setFieldInfos: Dispatch<SetStateAction<IEsriFieldInfo[]>>;
    isMobile: boolean;
    isIFrame: boolean;
    getFieldAlias: (fieldName: string) => string;
    getFieldValue: (feature: Feature, fieldName: string) => any;
    getDateFormated: (time: number) => string;
    zoomToSourceFeatures: number;
    setZoomToSourceFeatures: Dispatch<SetStateAction<number>>;
    locateFeature: ILocatedFeature;
    setLocateFeature: Dispatch<SetStateAction<Feature>>;
    layersMenuVisible: boolean;
    toggleLayersMenuVisible: () => void;
    enabledLayers: string[];
    setEnabledLayers: Dispatch<SetStateAction<string[]>>;
    registerFeature: Feature;
    setRegisterFeature: Dispatch<SetStateAction<Feature>>;
    info: IInfo;
    setInfo: Dispatch<SetStateAction<IInfo>>;
    getStoredSettings: () => IStoredSettings;
    scaleWarning: boolean;
    setScaleWarning: Dispatch<SetStateAction<boolean>>;
    clickedFeatures: Feature[];
    setClickedFeatures: (features: Feature[]) => void;
    drawType: DrawType;
    setDrawType: Dispatch<SetStateAction<DrawType>>;
    identifiedFeatures: Feature[];
    setIdentifiedFeatures: Dispatch<SetStateAction<Feature[]>>;
}

export interface IApplyFeature {
    feature: Feature;
    isRight?: boolean;
}
export interface ILocatedFeature {
    feature: Feature;
    timestamp: number;
}

const AppContext = createContext<IAppContext>(null);

export const useAppContext = (): IAppContext => useContext(AppContext);

export const AppContextProvider: FC = (props: React.PropsWithChildren<IAppContext>) => {
    const getStoredSettings = (): IStoredSettings => {
        const notInTocLayers = config.layers.map((layer) => !layer.showInToc && layer.id).filter((l) => l);
        const storedSettings: IStoredSettings = {
            layers: localStorage.getItem("layers")
                ? [...JSON.parse(localStorage.getItem("layers")), ...notInTocLayers]
                : config.layers.map((layer) => layer.visible && layer.id).filter((l) => l),
            basemap: localStorage.getItem("basemap") && JSON.parse(localStorage.getItem("basemap"))
        };

        return storedSettings;
    };
    const getBasemap = getStoredSettings().basemap
        ? getStoredSettings().basemap
        : config.basemaps.find((b) => b.visible).id;

    const [hasMap, setHasMap] = useState<boolean>(false);
    // const [hasMap, setHasMap] = useState<boolean>(true);
    const [basemap, setBasemap] = useState<Basemaps>(getBasemap as Basemaps);
    const [activeTab, setActiveTab] = useState<number>(1);
    const [fieldInfos, setFieldInfos] = useState<IEsriFieldInfo[]>();
    const [zoomToSourceFeatures, setZoomToSourceFeatures] = useState<number>(null);
    const [locateFeature, setLocateFeature] = useState<ILocatedFeature>(null);
    const [layersMenuVisible, setLayersMenuVisible] = useState<boolean>(false);
    const [enabledLayers, setEnabledLayers] = useState<string[]>([]);
    const [registerFeature, setRegisterFeature] = useState<Feature<Geometry>>();
    const [info, setInfo] = useState<IInfo>(null);
    const [scaleWarning, setScaleWarning] = useState<boolean>(true);
    const [clickedFeatures, setClickedFeatures] = useState<Feature<Geometry>[]>();
    const [drawType, setDrawType] = useState<DrawType>();
    const [identifiedFeatures, setIdentifiedFeatures] = useState<Feature<Geometry>[]>();

    const clickedFeaturesRef = useRef<Feature<Geometry>[]>();

    const isMobile =
        navigator.maxTouchPoints > 0 || navigator.platform === "iPhone" || navigator.platform === "iPad" || iniFrame();

    const isIFrame = iniFrame();

    const layersMenuVisibleRef = useRef<boolean>(false);

    useEffect(() => {
        const layerId =
            drawType === DrawType.PolygonProperty
                ? "cadaster"
                : drawType === DrawType.PolylineLvmRoad
                ? "roadsLVM"
                : null;

        layerId && !enabledLayers.includes(layerId) && setEnabledLayers([...enabledLayers, layerId]);
    }, [drawType]);

    const getFieldAlias = (fieldName: string) => fieldInfos && fieldInfos.find((f) => f.name === fieldName).alias;

    const getFieldValue = (feature: Feature, fieldName: string) => {
        const field = fieldInfos && fieldInfos.find((f) => f.name === fieldName);
        if (!field) {
            return undefined;
        }
        const value = feature.get(field.name);
        if (fieldName === "EVENTDATE" && value) {
            return getDateFormated(value) + " " + getTimeFormated(value);
        }
        if (field.type === "esriFieldTypeDate" && value) {
            return getDateFormated(value);
        }
        if (field.domain && field.domain.type === "codedValue" && value) {
            const codedValue = field.domain.codedValues.find((codedValue) => codedValue.code == value);
            return codedValue && codedValue.name ? codedValue.name : null;
        }
        return value;
    };

    const getDateFormated = (value: number) => {
        const date = new Date(value);
        return (
            date.getUTCDate().toString().padStart(2, "0") +
            "." +
            (date.getUTCMonth() + 1).toString().padStart(2, "0") +
            "." +
            date.getUTCFullYear()
        );
    };

    const toggleLayersMenuVisible = () => {
        layersMenuVisibleRef.current = !layersMenuVisibleRef.current;
        setLayersMenuVisible(layersMenuVisibleRef.current);
    };

    const changeActiveTab = (tab: number) => {
        setActiveTab(tab);
    };

    const changeRegisterFeature = (feature: Feature) => {
        if (feature) {
            // generateSnapshots(feature, basemap, setSnapshotBig, setSnapshot);
        }
        setRegisterFeature(feature);
    };

    const prepareLocateFeature = useCallback((feature) => {
        setLocateFeature({ feature, timestamp: Date.now() });
    }, []);

    const prepareClickedFeatures = (features: Feature<Geometry>[]) => {
        clickedFeaturesRef.current &&
            clickedFeaturesRef.current.length &&
            clickedFeaturesRef.current.map((f) => f.set("CLICKED", false));
        setClickedFeatures(features);
        clickedFeaturesRef.current = features;
    };

    const values = {
        hasMap,
        setHasMap,
        basemap,
        setBasemap,
        activeTab,
        setActiveTab: changeActiveTab,
        fieldInfos,
        setFieldInfos,
        isMobile,
        isIFrame,
        getFieldAlias,
        getFieldValue,
        getDateFormated,
        zoomToSourceFeatures,
        setZoomToSourceFeatures,
        locateFeature,
        setLocateFeature: prepareLocateFeature,
        layersMenuVisible,
        toggleLayersMenuVisible,
        enabledLayers,
        setEnabledLayers,
        registerFeature,
        setRegisterFeature: changeRegisterFeature,
        info,
        setInfo,
        getStoredSettings,
        scaleWarning,
        setScaleWarning,
        clickedFeatures,
        setClickedFeatures: prepareClickedFeatures,
        drawType,
        setDrawType,
        identifiedFeatures,
        setIdentifiedFeatures
    };

    return <AppContext.Provider value={values}>{props.children}</AppContext.Provider>;
};

const iniFrame = () => {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true;
    }
};

const getTimeFormated = (value: number) => {
    const date = new Date(value);
    return date.getUTCHours().toString().padStart(2, "0") + ":" + date.getUTCMinutes().toString().padStart(2, "0");
};
